import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { setActiveCategory, setCompletedCategories, resetQuizProgress } from '../../redux/slices/forcedChoiceQuizSlice';
import { addAssessment } from '../../redux/slices/coachSlice';
import downloadPDF from '../../utils/pdf/downloadPDF';
import CoachRosterPDF from '../../utils/pdf/CoachRosterPDF';
import { TRAIT_CATEGORIES } from '../../utils/traitCategories';
import useFetchTraitAssessmentProgress from '../../hooks/quiz/useFetchTraitAssessmentProgress';
import useSubmitTraitAssessment from '../../hooks/quiz/useSubmitTraitAssessment';

import { getDaysUntilEligible } from '../../utils/traitCooldown';
import TraitCooldownModal from '../../components/TraitCooldownModal/TraitCooldownModal';

import WelcomeMessage from '../../components/WelcomeMessage/WelcomeMessage';
import QuizProgressBar from '../../components/QuizProgressBar/QuizProgressBar';
import DownloadPDFButton from '../../components/DownloadPDFButton/DownloadPDFButton';
import InviteAthleteButton from '../../components/InviteAthleteButton/InviteAthleteButton';
import AthleteInviteModal from '../../components/AthleteInviteModal/AthleteInviteModal';
import StartQuizButton from '../../components/StartQuizButton/StartQuizButton';
import GameIQModal from '../../components/GameIQModal/GameIQModal';
// import StartETSQuizButton from '../../components/StartQuizButton/StartETSQuizButton';
import StartTraitQuizButton from '../../components/StartQuizButton/StartTraitQuizButton';
// import GScoreGraphArea from '../../components/GScoreGraphArea/GScoreGraphArea';
import QuizResultsArea from '../../components/QuizResultsArea/QuizResultsArea';
import LearningLibraryCard from '../../components/LearningLibraryCard/LearningLibraryCard';
import AthleteLeaderboard from '../../components/AthleteLeaderboard/AthleteLeaderboard';
import TraitProfileCard from '../../components/TraitProfileCard/TraitProfileCard';
import ToastPopup from '../../components/ToastPopup/ToastPopup';
import Dropdown from '../../components/Dropdown/Dropdown';
import Tooltip from '../../components/Tooltip/Tooltip';
import NowViewingBanner from '../../components/NowViewingBanner/NowViewingBanner';
import Copyright from '../../components/Copyright/Copyright';
import DigitalClock from '../../components/DigitalClock/DIgitalClock';

import useToast from '../../hooks/ui/useToast';
import { useInviteAthlete } from '../../hooks/coachRequest/useInviteAthlete';
import { setQuizInfo, setSelectedSport } from '../../redux/slices/quizSlice';

import styles from '../AthleteDashboard/AthleteDashboard.module.css'; // Reusing same styles

const allSports = ['basketball', 'football'];
const allLevels = [1, 2, 3, 4, 5];
const TOTAL_LEVELS = 5;

const CoachDashboard = () => {
  useOutletContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isCooldownModalOpen, setIsCooldownModalOpen] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);

  const {
    profile,
    assessments,
    selectedAthlete,
    selectedAthleteAssessments,
    athletesWithAssessments,
    loading,
    error,
  } = useSelector((state) => state.coach);

  const selectedSport = useSelector((state) => state.quiz.sport);
  const { fetchAllTraitProgress } = useFetchTraitAssessmentProgress();
  const { submitAssessment } = useSubmitTraitAssessment();

  const { inviteAthlete } = useInviteAthlete();
  const { toast, showToast, closeToast } = useToast(3000);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isGameIQModalOpen, setIsGameIQModalOpen] = useState(false);

  const handleDownload = async () => {
    if (!athletesWithAssessments?.length) return;
    const coachName = profile ? `${profile.firstName} ${profile.lastName}` : 'Coach';
    const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    await downloadPDF(
      <CoachRosterPDF
        athletesWithAssessments={athletesWithAssessments}
        coachName={coachName}
        sport={selectedSport}
        generatedDate={generatedDate}
      />,
      `Gymnaze_Roster_${selectedSport || 'All'}_${new Date().toISOString().slice(0, 10)}.pdf`
    );
  };

  const handleInvite = async (email) => {
    try {
      const result = await inviteAthlete(email);
      const message = result.type === 'invite'
        ? `Invite sent to ${email}`
        : `Connection request sent to ${email}`;
      showToast(message, 'success');
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    }
    setIsInviteModalOpen(false);
  };

  const getCoachLevelOptions = () =>
    allLevels.map(level => ({ label: `Level ${level}`, value: level }));

  const handleGameIQBegin = (sport, level) => {
    dispatch(setQuizInfo({ sport, level, questions: [] }));
    navigate('/dashboard/quiz');
  };

  // const handleStartETSQuizClick = () => {
  //   navigate('/dashboard/gscore-quiz');
  // };

  const currentAssessments = selectedAthlete
    ? selectedAthleteAssessments
    : assessments;

  const completedLevels = useMemo(() => {
    if (!currentAssessments || !selectedSport) return [];
    const levels = currentAssessments
      .filter((a) => a.sport === selectedSport && a.passed)
      .map((a) => a.level);
    return [...new Set(levels)];
  }, [currentAssessments, selectedSport]);

  const progress = Math.round((completedLevels.length / TOTAL_LEVELS) * 100);

  const gameIQAttempts = useMemo(() => {
    return currentAssessments?.filter((a) => a.type === 'game-iq') || [];
  }, [currentAssessments]);

  // const latestETSAttempt = useMemo(() => {
  //   if (!currentAssessments) return null;
  //   const etsAttempts = currentAssessments.filter((a) => a.type === 'ets-personality');
  //   if (etsAttempts.length === 0) return null;
  //   etsAttempts.sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken));
  //   return etsAttempts[0];
  // }, [currentAssessments]);

  const latestTraitAssessment = useMemo(() => {
    if (!currentAssessments) return null;
    return currentAssessments
      .filter((a) => a.type === 'trait-assessment')
      .sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken))[0] || null;
  }, [currentAssessments]);

  const handleStartTraitQuizClick = async () => {

    dispatch(setCompletedCategories([]));

    if (latestTraitAssessment) {
      const days = getDaysUntilEligible(latestTraitAssessment.dateTaken);

      if (days > 0) {
        setDaysRemaining(days -1 );
        setIsCooldownModalOpen(true);
        return;
      }
    }
  
    try {
      const { data } = await fetchAllTraitProgress();
      const completed = data?.getAllTraitAssessmentProgress
        ?.filter((p) => p.isCompleted)
        .map((p) => p.category) || [];
  
      dispatch(setCompletedCategories(completed));
  
      const nextCategory = TRAIT_CATEGORIES.find(
        (cat) => !completed.includes(cat)
      );
  
      if (nextCategory) {
        dispatch(resetQuizProgress());
        dispatch(setActiveCategory(nextCategory));
        navigate('/dashboard/trait-quiz');
      } else if (!latestTraitAssessment) {
        try {
          const result = await submitAssessment();
          const attempt = result?.data?.submitTraitAssessment;
          if (attempt) dispatch(addAssessment(attempt));
          showToast('Assessment submitted successfully!', 'success');
        } catch (err) {
          showToast('Submission failed. Please try again.', 'error');
        }
      }
    } catch (err) {
      console.error('Failed to load trait quiz progress:', err);
    }
  };

  if (loading) return <p className={styles.loading}>Loading your dashboard...</p>;
  if (error) return <p className={styles.error}>Error: {error}</p>;

  return (
    <div className={styles.dashboardWrapper}>
      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />

      <div className={styles.topRow}>
        {selectedAthlete ? (
          <NowViewingBanner athlete={selectedAthlete} />
        ) : (
          <WelcomeMessage
            name={profile?.firstName || 'Coach'}
            sport={selectedSport}
          />
        )}

        <QuizProgressBar progress={progress} />

        <div className={styles.startQuizWrapper}>
          <StartQuizButton onClick={() => setIsGameIQModalOpen(true)} />
        </div>
        {/* <StartETSQuizButton onClick={handleStartETSQuizClick} /> */}
        <StartTraitQuizButton onClick={handleStartTraitQuizClick} />
        <InviteAthleteButton onClick={() => setIsInviteModalOpen(true)} />
        <DownloadPDFButton onClick={handleDownload} />
      </div>

      <div style={{ marginTop: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Dropdown
          options={allSports.map((sport) => ({
            label: sport.charAt(0).toUpperCase() + sport.slice(1),
            value: sport,
          }))}
          value={selectedSport}
          onChange={(value) => dispatch(setSelectedSport(value))}
          placeholder="Select Sport"
        />
        <Tooltip text="Filters the progress bar by sport">
          <span style={{ fontSize: '1rem', opacity: 0.5, cursor: 'default', color: 'var(--color-text)' }}>ⓘ</span>
        </Tooltip>
      </div>
      
      <div className={styles.secondRow}>
        <TraitProfileCard
          profile={latestTraitAssessment?.profile || null}
          confidence={latestTraitAssessment?.confidence || 0}
          blend={latestTraitAssessment?.blend || null}
          id={latestTraitAssessment?._id || null}
        />
        <QuizResultsArea attempts={gameIQAttempts} />
        {/* <GScoreGraphArea etsAttempt={latestETSAttempt} /> */}
        <AthleteLeaderboard />
        <LearningLibraryCard />
      </div>

      <div className={styles.thirdRow}>
        <Copyright />
        <DigitalClock />
      </div>

      <AthleteInviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInvite}
      />
      <TraitCooldownModal
        isOpen={isCooldownModalOpen}
        daysRemaining={daysRemaining}
        onClose={() => setIsCooldownModalOpen(false)}
      />
      <GameIQModal
        isOpen={isGameIQModalOpen}
        onClose={() => setIsGameIQModalOpen(false)}
        onBegin={handleGameIQBegin}
        sportOptions={allSports.map(sport => ({
          label: sport.charAt(0).toUpperCase() + sport.slice(1),
          value: sport,
        }))}
        getLevelOptions={getCoachLevelOptions}
        initialSport={selectedSport}
      />
    </div>
  );
};

export default CoachDashboard;